import { CompareSidebar } from '@/components/AssetSidebar/CompareSidebar';
import { api } from '@/services/api';
import { createUseStyles } from '@/services/createUseStyles';
import { useTenants } from '@/services/tenants/useTenants';
import { Box, styled, Theme } from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BodyText } from '@/components/BodyText';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AssetState } from '@/api/generatedApi';
import { TitleText } from '@/components/TitleText';
import { getAssetName } from '@/utils/assets';
import { MatchData } from '@/types/matchData';
import { getNumberParameter } from '@/utils/queryParameters';
import { SearchType } from '@/components/Search/types';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { GridCompareAssets } from '@/components/ComparePage/gridCompareAssets';
import { CompareTable } from '@/components/ComparePage/CompareTable';
import { useThemeMode } from '@/hooks/useThemeMode';

const useStyles = createUseStyles((theme: Theme) => ({
  ComparePage_root: {
    flex: 1,
    minWidth: '400px',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  ComparePage_iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  ComparePage_fadeLoader: {
    position: 'absolute',
    top: '8px',
    left: '0',
    bottom: '8px',
    right: '8px',
    animation: '$fadeLoader 1.5s ease-in-out infinite',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '@keyframes fadeLoader': {
    '0%': {
      backgroundColor: 'transparent',
    },
    '50%': {
      backgroundColor: theme.palette.action.hover,
    },
    '100%': {
      backgroundColor: 'transparent',
    },
  },
}));

const ZeroStateBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
});

const PageWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
});

const PageContent = styled(Box)({
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-start',
  overflow: 'hidden',
  height: '100%',
  minHeight: 0,
  minWidth: 0,
  width: '100%',
});

const ContentArea = styled(Box)({
  flex: 1,
  minWidth: 0,
  maxWidth: '100%',
  width: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '8px',
});

export const ComparePage = () => {
  const { isNewCompareAssetsUiEnabled } = useFeatureFlags();

  if (isNewCompareAssetsUiEnabled) {
    return <GridCompareAssets />;
  }

  return <ViewerCompareUI />;
};

const ViewerCompareUI = () => {
  const c = useStyles();
  const [searchParameters] = useSearchParams();
  const { getTenantId } = useTenants();
  const { resolvedMode } = useThemeMode();
  const [error, setError] = useState<string>();
  const iframeReference = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const asset1Id = searchParameters.get('asset1Id') ?? undefined;
  const asset2Id = searchParameters.get('asset2Id') ?? undefined;
  const tenant1Id = searchParameters.get('tenant1Id') ?? getTenantId();
  const tenant2Id = searchParameters.get('tenant2Id') ?? undefined;
  const searchType: SearchType | undefined = searchParameters.get('searchType')
    ? (searchParameters.get('searchType') as SearchType)
    : undefined;

  const hasBothAssetIds = !!(asset1Id && asset2Id);

  const matchData: MatchData = {
    searchType,
    matchPercentage: getNumberParameter(searchParameters, 'matchPercentage'),
    forwardMatchPercentage: getNumberParameter(searchParameters, 'forwardMatchPercentage'),
    reverseMatchPercentage: getNumberParameter(searchParameters, 'reverseMatchPercentage'),
  };

  const { asset: asset1, isLoading: asset1IsLoading } = useGetAsset({ assetId: asset1Id, assetTenantId: tenant1Id });
  const { asset: asset2, isLoading: asset2IsLoading } = useGetAsset({ assetId: asset2Id, assetTenantId: tenant2Id });

  const getCompareTitle = () => {
    const names = [asset1, asset2]
      .filter((asset): asset is NonNullable<typeof asset> => asset !== undefined)
      .map(asset => getAssetName(asset));
    if (names.length === 2) {
      return `Compare ${names[0]} & ${names[1]}`;
    }
    if (names.length === 1) {
      return `Compare ${names[0]}`;
    }
    return 'Compare';
  };

  usePageTitle(getCompareTitle());

  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  const fetchComparisonViewer = async () => {
    setError(undefined);
    setIframeLoaded(false);
    try {
      const apiLocation = `${import.meta.env.VITE_API_BASE_URL}/v3`;

      const {
        data: { token },
      } = await api.viewer.viewerToken();

      let iframeSource = `${apiLocation}/tenants/${tenant1Id}/viewer/comparison?token=${token}&asset1Id=${asset1Id}&asset2Id=${asset2Id}&theme=${resolvedMode}`;

      if (tenant2Id) {
        iframeSource += `&tenant2Id=${tenant2Id}`;
      }

      if (iframeReference.current) {
        iframeReference.current.src = iframeSource;
      }
    } catch (error) {
      setError(error instanceof AxiosError ? error.message : 'Error fetching comparison viewer');
    }
  };
  useEffect(() => {
    if (asset1Id && asset2Id && tenant1Id && tenant2Id && !asset1IsLoading && !asset2IsLoading) {
      fetchComparisonViewer();
    }
  }, [asset1Id, asset2Id, tenant1Id, tenant2Id, asset1IsLoading, asset2IsLoading]);

  useEffect(() => {
    const iframe = iframeReference.current;
    if (!iframe?.contentWindow || !iframe.src) return;
    const targetOrigin = new URL(iframe.src).origin;
    iframe.contentWindow.postMessage({ type: 'physna:theme-mode', mode: resolvedMode }, targetOrigin);
  }, [resolvedMode]);

  if (
    !asset1IsLoading &&
    !asset2IsLoading &&
    asset1 &&
    asset2 &&
    (asset1.state !== AssetState.Finished || asset2.state !== AssetState.Finished)
  ) {
    return (
      <ZeroStateBox>
        <TitleText>Error</TitleText>
        <BodyText color='text.secondary'>
          {asset1.state === AssetState.Finished
            ? `${getAssetName(asset2)} is ${asset2.state}`
            : `${getAssetName(asset1)} is ${asset1.state}`}
        </BodyText>
      </ZeroStateBox>
    );
  }

  return (
    <PageWrapper>
      <PageContent>
        <CompareSidebar
          asset1Id={asset1Id}
          asset2Id={asset2Id}
          asset1TenantId={tenant1Id}
          asset2TenantId={tenant2Id}
          matchData={matchData}
        />
        <ContentArea>
          {hasBothAssetIds ? (
            <Box position='relative' width='100%' height='100%'>
              {!iframeLoaded && (
                <Box className={c.ComparePage_fadeLoader}>
                  <BodyText color='text.secondary'>...</BodyText>
                </Box>
              )}
              <iframe
                ref={iframeReference}
                className={c.ComparePage_iframe}
                title='3D Model Comparison Viewer'
                onLoad={handleIframeLoad}
              />
              {error && <div>{error}</div>}
            </Box>
          ) : (
            <CompareTable />
          )}
        </ContentArea>
      </PageContent>
    </PageWrapper>
  );
};
